<template>
  <section class="content-panel">
    <div class="section-head">
      <div>
        <h2>广告记录</h2>
        <p>{{ description }}</p>
      </div>
      <div class="header-right">
        <el-tag type="info" effect="plain">共 {{ total }} 条</el-tag>
        <el-tag v-if="!hideTotalRevenue" type="success" effect="plain">总金币 {{ totalRevenueFormatted }}</el-tag>
      </div>
    </div>

    <div class="filter-bar">
      <el-form :inline="true" class="filter-form" @submit.prevent>
        <el-form-item label="时间">
          <el-date-picker
            v-model="f.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :shortcuts="dateShortcuts"
            value-format="YYYY-MM-DD"
            :clearable="false"
            style="width: 280px"
          />
        </el-form-item>
        <el-form-item v-if="showCustomerFilter" label="客户">
          <CustomerRemoteSelect
            v-model="f.customerId"
            :token="token"
            placeholder="全部客户"
            width="180px"
            @select="onCustomerSelect"
          />
        </el-form-item>
        <el-form-item v-if="showAppFilter" label="应用">
          <el-select v-model="f.appId" placeholder="全部应用（总金币）" clearable style="width: 240px">
            <el-option
              v-for="app in apps"
              :key="app.id"
              :label="appLabel(app)"
              :value="app.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="用户ID">
          <el-input v-model="f.userId" clearable placeholder="主键ID" style="width: 140px" />
        </el-form-item>
        <el-form-item label="设备ID">
          <el-input v-model="f.deviceId" clearable placeholder="设备标识" style="width: 180px" />
        </el-form-item>
        <el-form-item label="广告平台">
          <el-select v-model="f.adPlatform" placeholder="全部平台" clearable style="width: 180px">
            <el-option v-for="platform in uniqueAdPlatforms" :key="platform" :label="adPlatformLabel(platform)" :value="platform" />
          </el-select>
        </el-form-item>
        <el-form-item label="广告类型">
          <el-select v-model="f.adType" placeholder="全部类型" clearable style="width: 160px">
            <el-option v-for="type in uniqueAdTypes" :key="type" :label="adTypeLabel(type)" :value="type" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="loading" @click="emitSearch">查询</el-button>
          <el-button @click="onClearFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-table v-loading="loading" :data="records" stripe>
      <el-table-column v-if="showCustomerColumn" label="客户" min-width="140">
        <template #default="{ row }">{{ customerName(row.customerId) }}</template>
      </el-table-column>
      <el-table-column v-if="showAppColumn" label="应用" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ resolveAppName(row.appId) }}</template>
      </el-table-column>
      <el-table-column label="用户ID" width="100">
        <template #default="{ row }">{{ row.userId ?? '-' }}</template>
      </el-table-column>
      <el-table-column prop="deviceId" label="设备ID" min-width="180" show-overflow-tooltip />
      <el-table-column label="广告类型" width="110">
        <template #default="{ row }">
          <el-tag size="small" :type="adTypeTag(row.adType)">{{ adTypeLabel(row.adType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="adPlatform" label="广告平台" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ adPlatformLabel(row.adPlatform) }}</template>
      </el-table-column>
      <el-table-column v-if="showReportId" prop="reportId" label="上报 ID" min-width="200" show-overflow-tooltip>
        <template #default="{ row }">{{ row.reportId || '-' }}</template>
      </el-table-column>
      <el-table-column label="金币" width="120">
        <template #default="{ row }"><span class="amount">{{ formatReward(row.reward, row.userRevenueRatio) }} 金币</span></template>
      </el-table-column>
      <el-table-column prop="createdAt" label="发生时间" min-width="180" />
      <template #empty><el-empty description="暂无广告记录" /></template>
    </el-table>

    <div class="panel-footer">
      <el-pagination
        v-model:current-page="innerPage"
        v-model:page-size="innerPageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        background
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import CustomerRemoteSelect from '@/components/CustomerRemoteSelect.vue'
import { formatReward } from '@/utils/reward'
import { adPlatformLabel } from '@/utils/adPlatform'

const props = defineProps({
  filters: { type: Object, required: true },
  records: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
  totalReward: { type: [Number, String], default: 0 },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  loading: { type: Boolean, default: false },
  groups: { type: Array, default: () => [] },
  apps: { type: Array, default: () => [] },
  uniqueAdPlatforms: { type: Array, default: () => [] },
  uniqueAdTypes: { type: Array, default: () => [] },
  token: { type: String, default: '' },
  customerName: { type: Function, default: () => '' },
  rememberCustomers: { type: Function, default: () => {} },
  onClearFilters: { type: Function, default: () => {} },
  description: { type: String, default: '' },
  showCustomerFilter: { type: Boolean, default: false },
  showAppFilter: { type: Boolean, default: false },
  showAppColumn: { type: Boolean, default: false },
  showCustomerColumn: { type: Boolean, default: false },
  showReportId: { type: Boolean, default: false },
  hideTotalRevenue: { type: Boolean, default: false }
})

const emit = defineEmits(['search', 'page-change'])

function onCustomerSelect(item) {
  if (item) props.rememberCustomers([item])
}

const innerPage = ref(props.page)
const innerPageSize = ref(props.pageSize)

watch(() => props.page, (v) => { innerPage.value = v })
watch(() => props.pageSize, (v) => { innerPageSize.value = v })

const f = reactive({
  dateRange: null,
  customerId: null,
  appId: null,
  groupId: null,
  deviceId: '',
  userId: '',
  adPlatform: null,
  adType: null
})

let syncing = false

watch(
  () => JSON.parse(JSON.stringify(props.filters)),
  (val) => {
    if (syncing) return
    syncing = true
    Object.assign(f, val)
    syncing = false
  },
  { immediate: true, deep: true, flush: 'sync' }
)

watch(
  f,
  () => {
    if (syncing) return
    syncing = true
    Object.assign(props.filters, f)
    syncing = false
  },
  { deep: true, flush: 'sync' }
)

const buildShortcutRange = (days = 0) => {
  const end = new Date()
  end.setHours(0, 0, 0, 0)
  const start = new Date(end)
  start.setDate(start.getDate() - days)
  return [start, end]
}

const dateShortcuts = [
  { text: '今日', value: () => buildShortcutRange(0) },
  {
    text: '昨日',
    value: () => {
      const day = new Date()
      day.setHours(0, 0, 0, 0)
      day.setDate(day.getDate() - 1)
      return [day, day]
    }
  },
  { text: '近7天', value: () => buildShortcutRange(6) },
  { text: '近30天', value: () => buildShortcutRange(29) }
]

const AD_TYPE_LABEL = {
  reward: '激励视频',
  interstitial: '插屏',
  banner: 'Banner'
}

const adTypeLabel = (type) => AD_TYPE_LABEL[type] || type || '-'
const adTypeTag = (type) => {
  const map = { reward: 'success', interstitial: 'warning', banner: 'info' }
  return map[type] || 'info'
}
const appLabel = (app) => {
  if (!app) return '-'
  return app.appName || '未命名应用'
}
const appNameMap = computed(() => {
  const map = {}
  for (const app of props.apps || []) {
    if (app?.id != null) {
      map[app.id] = appLabel(app)
    }
  }
  return map
})
const resolveAppName = (appId) => {
  if (appId == null || appId === '') return '-'
  return appNameMap.value[appId] || appNameMap.value[String(appId)] || '-'
}
const totalRevenueFormatted = computed(() => formatReward(props.totalReward))

function emitSearch() {
  emit('search')
}

function onPageChange(page) {
  emit('page-change', { page, size: innerPageSize.value })
}

function onSizeChange(size) {
  emit('page-change', { page: 1, size })
}
</script>

<style scoped>
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-form {
  margin: 0;
}

.amount {
  color: #dc2626;
  font-weight: 600;
}
</style>
